/**
 * PresetDetailPage - View full preset details with reviews
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronLeft,
  Star,
  ArrowUp,
  Download,
  Music,
  Sliders,
  User,
  Calendar,
  MessageSquare,
  Send,
  ThumbsUp,
  AlertCircle,
  Check,
  Sparkles,
  Volume2,
} from 'lucide-react';
import { PresetPreviewPlayer } from '../components/marketplace/PresetPreviewButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          disabled={!interactive}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors`}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hoverRating || rating)
                ? 'text-amber-500 fill-amber-500'
                : 'text-zinc-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review }) => {
  return (
    <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
            <User className="w-4 h-4 text-violet-400" />
          </div>
          <div>
            <p className="font-medium text-white text-sm">{review.user_name}</p>
            <p className="text-xs text-zinc-500">
              {new Date(review.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>
      <h4 className="font-medium text-white mb-1">{review.title}</h4>
      <p className="text-sm text-zinc-400 whitespace-pre-wrap">{review.content}</p>
      {review.helpful_count > 0 && (
        <div className="flex items-center gap-1 mt-3 text-xs text-zinc-500">
          <ThumbsUp className="w-3 h-3" />
          {review.helpful_count} found this helpful
        </div>
      )}
    </div>
  );
};

// Plugin Chain Display Component
const PluginChainDisplay = ({ plugins }) => {
  if (!plugins || plugins.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <Sliders className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No plugins in chain</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {plugins.map((plugin, index) => (
        <div
          key={plugin.id || index}
          className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              plugin.enabled !== false ? 'bg-violet-500/20' : 'bg-zinc-700/50'
            }`}>
              <span className={`font-mono text-sm ${
                plugin.enabled !== false ? 'text-violet-400' : 'text-zinc-500'
              }`}>
                {index + 1}
              </span>
            </div>
            <div>
              <p className={`font-medium ${plugin.enabled !== false ? 'text-white' : 'text-zinc-500'}`}>
                {plugin.name}
              </p>
              <p className="text-xs text-zinc-500">{plugin.type}</p>
            </div>
          </div>
          <div className={`text-xs px-2 py-0.5 rounded-full ${
            plugin.enabled !== false 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-zinc-700/50 text-zinc-500'
          }`}>
            {plugin.enabled !== false ? 'Active' : 'Bypassed'}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Component
export const PresetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [preset, setPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);
  const [hasUpvoted, setHasUpvoted] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewContent, setReviewContent] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Fetch preset details
  const fetchPreset = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/marketplace/presets/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPreset(data);
      } else if (response.status === 404) {
        toast.error('Preset not found');
        navigate('/marketplace');
      }
    } catch (error) {
      console.error('Failed to fetch preset:', error);
      toast.error('Failed to load preset');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  
  useEffect(() => {
    fetchPreset();
  }, [fetchPreset]);
  
  // Handle download
  const handleDownload = async () => {
    if (!user) {
      toast.error('Please log in to download presets');
      navigate('/');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${id}/download?user_id=${user.id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('importedPreset', JSON.stringify(data));
        toast.success('Preset downloaded! Open it in the studio to apply.');
        navigate('/studio');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download preset');
    }
  };
  
  // Handle rating
  const handleRate = async (rating) => {
    if (!user) {
      toast.error('Please log in to rate');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${id}/rate?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rating })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUserRating(rating);
        setPreset(prev => ({ ...prev, average_rating: data.new_average }));
        toast.success('Rating submitted!');
      }
    } catch (error) {
      console.error('Rating error:', error);
      toast.error('Failed to submit rating');
    }
  };
  
  // Handle upvote
  const handleUpvote = async () => {
    if (!user) {
      toast.error('Please log in to upvote');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${id}/upvote?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHasUpvoted(!hasUpvoted);
        setPreset(prev => ({ ...prev, upvote_count: data.upvote_count }));
        toast.success(hasUpvoted ? 'Upvote removed' : 'Upvoted!');
      }
    } catch (error) {
      console.error('Upvote error:', error);
    }
  };
  
  // Handle review submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please log in to review');
      return;
    }
    
    if (!reviewTitle.trim() || !reviewContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmittingReview(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${id}/reviews?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rating: reviewRating,
            title: reviewTitle.trim(),
            content: reviewContent.trim()
          })
        }
      );
      
      if (response.ok) {
        toast.success('Review submitted!');
        setShowReviewForm(false);
        setReviewTitle('');
        setReviewContent('');
        fetchPreset(); // Refresh to get new review
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Review error:', error);
      toast.error(error.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!preset) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-zinc-400">Preset not found</h2>
          <Button onClick={() => navigate('/marketplace')} className="mt-4">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-zinc-800 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketplace')}
              className="text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold truncate">{preset.name}</h1>
              <p className="text-sm text-zinc-400">
                by{' '}
                <button
                  onClick={() => navigate(`/marketplace/user/${preset.author_id}`)}
                  className="hover:text-violet-400 hover:underline transition-colors"
                  data-testid="author-profile-link"
                >
                  {preset.author_name}
                </button>
                {' '}• {new Date(preset.created_at).toLocaleDateString()}
              </p>
            </div>
            <Button
              onClick={handleDownload}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="download-preset-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
            >
              {/* Type Banner */}
              <div className={`h-32 flex items-center justify-center ${
                preset.preset_type === 'master'
                  ? 'bg-gradient-to-br from-amber-600/30 to-orange-600/30'
                  : 'bg-gradient-to-br from-cyan-600/30 to-blue-600/30'
              }`}>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  preset.preset_type === 'master'
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : 'bg-gradient-to-br from-cyan-500 to-blue-600'
                }`}>
                  {preset.preset_type === 'master' ? (
                    <Sliders className="w-10 h-10 text-white" />
                  ) : (
                    <Music className="w-10 h-10 text-white" />
                  )}
                </div>
                
                {preset.is_featured && (
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30 text-sm flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    Featured
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-6">
                {/* Audio Preview Player */}
                <div className="mb-6">
                  <PresetPreviewPlayer presetId={preset.id} presetName={preset.name} />
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    preset.preset_type === 'master'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-cyan-500/20 text-cyan-400'
                  }`}>
                    {preset.preset_type === 'master' ? 'Master Preset' : 'Track Preset'}
                  </span>
                  
                  {preset.genre && (
                    <span className="px-3 py-1 rounded-full text-sm bg-violet-500/10 text-violet-400">
                      {preset.genre}
                    </span>
                  )}
                  {preset.instrument_type && (
                    <span className="px-3 py-1 rounded-full text-sm bg-cyan-500/10 text-cyan-400">
                      {preset.instrument_type}
                    </span>
                  )}
                  {preset.style && (
                    <span className="px-3 py-1 rounded-full text-sm bg-rose-500/10 text-rose-400">
                      {preset.style}
                    </span>
                  )}
                </div>
                
                <p className="text-zinc-300 whitespace-pre-wrap">
                  {preset.description || 'No description provided.'}
                </p>
                
                {preset.tags && preset.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {preset.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs bg-zinc-800 text-zinc-400">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
            
            {/* Plugin Chain */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sliders className="w-5 h-5 text-violet-500" />
                Plugin Chain ({preset.plugin_chain?.length || 0} plugins)
              </h2>
              <PluginChainDisplay plugins={preset.plugin_chain} />
            </motion.div>
            
            {/* Reviews Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-500" />
                  Reviews ({preset.reviews?.length || 0})
                </h2>
                {user && !showReviewForm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowReviewForm(true)}
                    className="text-violet-400"
                  >
                    Write Review
                  </Button>
                )}
              </div>
              
              {/* Review Form */}
              {showReviewForm && (
                <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="mb-4">
                    <label className="text-sm text-zinc-400 mb-2 block">Your Rating</label>
                    <StarRating
                      rating={reviewRating}
                      onRate={setReviewRating}
                      interactive
                      size="lg"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-sm text-zinc-400 mb-2 block">Title</label>
                    <Input
                      value={reviewTitle}
                      onChange={(e) => setReviewTitle(e.target.value)}
                      placeholder="Summarize your review..."
                      className="bg-zinc-800 border-zinc-700"
                      maxLength={100}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-sm text-zinc-400 mb-2 block">Review</label>
                    <textarea
                      value={reviewContent}
                      onChange={(e) => setReviewContent(e.target.value)}
                      placeholder="Share your experience with this preset..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500 min-h-[100px] resize-none"
                      minLength={10}
                      maxLength={2000}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowReviewForm(false)}
                      className="text-zinc-400"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submittingReview}
                      className="bg-violet-600 hover:bg-violet-500"
                    >
                      {submittingReview ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Review
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
              
              {/* Reviews List */}
              {preset.reviews && preset.reviews.length > 0 ? (
                <div className="space-y-4">
                  {preset.reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No reviews yet. Be the first to share your thoughts!</p>
                </div>
              )}
            </motion.div>
          </div>
          
          {/* Right Column - Stats & Actions */}
          <div className="space-y-6">
            {/* Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
              <h3 className="font-semibold mb-4">Stats</h3>
              
              {/* Rating */}
              <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                <span className="text-zinc-400">Rating</span>
                <div className="flex items-center gap-2">
                  <StarRating rating={Math.round(preset.average_rating || 0)} />
                  <span className="text-white font-medium">
                    {preset.average_rating?.toFixed(1) || '0.0'}
                  </span>
                  <span className="text-zinc-500 text-sm">({preset.rating_count || 0})</span>
                </div>
              </div>
              
              {/* Downloads */}
              <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                <span className="text-zinc-400">Downloads</span>
                <span className="text-white font-medium flex items-center gap-2">
                  <Download className="w-4 h-4 text-zinc-500" />
                  {preset.download_count || 0}
                </span>
              </div>
              
              {/* Upvotes */}
              <div className="flex items-center justify-between py-3">
                <span className="text-zinc-400">Upvotes</span>
                <span className="text-white font-medium flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-zinc-500" />
                  {preset.upvote_count || 0}
                </span>
              </div>
            </motion.div>
            
            {/* Actions Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
              <h3 className="font-semibold mb-4">Actions</h3>
              
              {/* Rate */}
              <div className="mb-4">
                <p className="text-sm text-zinc-400 mb-2">Rate this preset</p>
                <StarRating
                  rating={userRating}
                  onRate={handleRate}
                  interactive={!!user}
                  size="lg"
                />
                {!user && (
                  <p className="text-xs text-zinc-500 mt-1">Log in to rate</p>
                )}
              </div>
              
              {/* Upvote */}
              <Button
                variant="outline"
                onClick={handleUpvote}
                disabled={!user}
                className={`w-full mb-4 ${hasUpvoted ? 'border-emerald-500 text-emerald-400' : ''}`}
              >
                <ArrowUp className={`w-4 h-4 mr-2 ${hasUpvoted ? 'text-emerald-400' : ''}`} />
                {hasUpvoted ? 'Upvoted' : 'Upvote'}
              </Button>
              
              {/* Download */}
              <Button
                onClick={handleDownload}
                className="w-full bg-violet-600 hover:bg-violet-500"
              >
                <Download className="w-4 h-4 mr-2" />
                Download & Apply
              </Button>
            </motion.div>
            
            {/* Author Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
              <h3 className="font-semibold mb-4">Created by</h3>
              <button
                onClick={() => navigate(`/marketplace/user/${preset.author_id}`)}
                className="flex items-center gap-3 w-full text-left hover:bg-zinc-800/50 rounded-lg p-2 -m-2 transition-colors"
                data-testid="author-card-link"
              >
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <p className="font-medium text-white hover:text-violet-400 transition-colors">{preset.author_name}</p>
                  <p className="text-xs text-zinc-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(preset.created_at).toLocaleDateString()}
                  </p>
                </div>
              </button>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PresetDetailPage;
